// @flow

import React, { type Node } from 'react';
import styled from 'styled-components';

import { useRedirect } from '@tectonics/hooks';
import { type LookAndFeelRootPropsConfirmedPayment } from '@tectonics/look-and-feel';
import { useIncomingPayment, getCountry, IncomingPaymentProvider, getRedirectUrlAfterPayment } from '@tectonics/prs/incoming-payment';
import { PaymentSpecProvider } from '@tectonics/prs/payment-spec';
import { AnalyticsContextProvider, AnalyticsCachedEvent } from '@tectonics/analytics';

import { LoadingWow } from '../../commons/molecules/loading';

export function ConfirmedPaymentPage({
  spec,
  incomingPayment,
  analytics,
}: LookAndFeelRootPropsConfirmedPayment): Node {
  useRedirect(getRedirectUrlAfterPayment(incomingPayment), 2000);

  return (
    <PaymentSpecProvider spec={spec}>
      <IncomingPaymentProvider incomingPayment={incomingPayment}>
        <AnalyticsContextProvider analytics={analytics}>
          <AnalyticsCachedEvent eventType="pageView" />
          <Page />
        </AnalyticsContextProvider>
      </IncomingPaymentProvider>
    </PaymentSpecProvider>
  );
}

function Page(): Node {
  return (
    <CardConfirmedWrapper data-test-id="page-confirmed-payment">
      <LoadingWow />
      <ConfirmedPageMessage />
    </CardConfirmedWrapper>
  );
}

function ConfirmedPageMessage(): Node {
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);

  switch(country){
    case 'br':
      return (
        <>
          <PaymentConfirmedMessage>O seu pagamento está sendo processado...</PaymentConfirmedMessage>
          <RedirectMessage> Aguarde enquanto te redirecionamos para o site em que a compra foi feita. :)</RedirectMessage>
        </>
      );

    case 'ar':
    case 'bo':
    case 'cl':
    case 'co':
    case 'ec':
    case 'mx':
    case 'pe':
    case 'py':
    case 'uy':
      return (
        <>
          <PaymentConfirmedMessage>Tu pago está siendo processado...</PaymentConfirmedMessage>
          <RedirectMessage> Espera mientras te redirecionamos al sitio donde la compra fue hecha. :)</RedirectMessage>
        </>
      );

    case 'za':
    case 'ke':
    case 'ng':
      return (
        <>
          <PaymentConfirmedMessage>Your payment is being processed...</PaymentConfirmedMessage>
          <RedirectMessage> Please wait while we redirect you to the site where the purchase was made. :)</RedirectMessage>
        </>
      );

    default:
      throw new Error(`Confirmed page translation not found for country: ${country}`);
  }
}

const PaymentConfirmedMessage = styled.div`
  margin-top: 40px;
  color: #2397d4;
  font-size: 22px;
  font-weight: 400;
  font-family: ${props => props.theme.fontFamily.display};
`;

const RedirectMessage = styled.div`
  margin-top: 5px;
  color: #5c5c5c;
  font-size: 14px;
  font-weight: 400;
  font-family: ${props => props.theme.fontFamily.sansSerif};
`;

export const CardConfirmedWrapper = styled.div`
  flex: 1 1 auto;
  text-align: center;
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  height: 100%;
  justify-content: center;
`;
